/**
 * Settings routes (Console V3 design T8624). Each line was checked against the back end:
 * the file and line of the handler are in the comment. Routes marked PROPOSED are not
 * served yet (they answer 404); the page shows the block's designed empty state until
 * they are. Confirm-first actions (handoff 10b) carry `confirm: true`.
 *
 * Reused from other pages rather than repeated here: governedSettings and governedStatus
 * (oversight-routes), frameworks and frameworkEnable (policy/overview routes),
 * pulseOncallV3 (system-pulse-routes).
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type { ConsentOnFile } from './connections-routes';
import type {
  AccessRequestsResponse,
  AdminControls,
  AdminControlsBody,
  AdminUserCreateBody,
  AdminUserCreated,
  AdminUserSessions,
  AdminUsersBulkResult,
  AdminUsersResponse,
  AdminUserUpdateBody,
  AdminUser,
  AiActExport,
  AlertPhone,
  AlertPhoneSent,
  ApiKeyCreated,
  ApiKeysResponse,
  DeviceTestResult,
  EnabledFrameworks,
  FacilityDevicesResponse,
  FacilityOrganizationBody,
  FacilityOrganizationResponse,
  FacilityContact,
  FacilityStaffResponse,
  MedRulesView,
  StaffCredential,
  StaffCredentialBody,
  FamilyLinksResponse,
  GateCheckResult,
  GovernedDefault,
  GovernedFacility,
  GovernedSaved,
  GuardianRules,
  GuardLevelResult,
  GuardStatus,
  HomePreferences,
  IntegrationCatalogResponse,
  IntegrationCreateBody,
  IntegrationsResponse,
  ModelRouting,
  NotifEventsResponse,
  NotifEventsSaved,
  NotifChannel,
  NotifPrefs,
  OncallRotationBody,
  OncallStepBody,
  OrgDeletionList,
  OrgDeletionRequest,
  OrgProfile,
  OrgProfileResponse,
  OrgProfileSaved,
  Passkey,
  PurgeSampleResult,
  PushDevicesResponse,
  RosterImportResult,
  RosterRow,
  RotateAllKeysResult,
  ScheduleResponse,
  ScheduleSaveBody,
  SettingsAlwaysResponse,
  SettingsHistoryResponse,
  SettingsSectionKey,
  SettingsSectionsResponse,
  SmsRolesResponse,
  TenantKeyRotated,
  TenantSettingsBody,
  TenantSettingsSaved,
  TestBatchResult,
  TestRunSummary,
  TestSimulateResult,
  TestStressResult,
  TrustedDevice,
  TwofaSessionEnroll,
  TwofaStatus,
  VoiceScribeSettings,
  WebhookRoutesResponse,
  GovernWebhookRoutesResponse,
  WebhookRouteRow,
  IntegrationTestResult,
  OrgUsage,
  LlmLimit,
} from './settings-types';

const A = AITHORA_PREFIX;

// ── Section map, history, the read-only rules (v3/settings.ts) ─────────────

/** GET /settings/sections (settings.ts:210): which sections this caller may see and change. */
export const settingsSections = route<SettingsSectionsResponse>()('GET', `${A}/settings/sections`, { honest: true });
/** GET /settings/always (settings.ts:218): "Rules that always apply" and "Always kept this way". */
export const settingsAlways = route<SettingsAlwaysResponse>()('GET', `${A}/settings/always`, { honest: true });
/** GET /settings/history?section=&limit= (settings.ts:240): one section's change history, newest first. */
export const settingsHistory = route<SettingsHistoryResponse, { section?: SettingsSectionKey; limit?: number; facilityId?: string }>()(
  'GET', `${A}/settings/history`, { honest: true });

// ── Danger Zone (settings.ts:306-479) ───────────────────────────────────────

/** GET /settings/org-deletion (settings.ts:306): Aithora staff only. */
export const settingsOrgDeletions = route<OrgDeletionList, { organizationId?: string }>()('GET', `${A}/settings/org-deletion`, { honest: true });
/** POST /settings/org-deletion (settings.ts:320): opens a request; the owner signs; a 30-day hold runs. */
export const settingsOrgDeletionCreate = route<{ real: true; request: OrgDeletionRequest }, void, { organizationId: string; reason: string; confirmRealResidents?: boolean; confirm: true }>()(
  'POST', `${A}/settings/org-deletion`, { honest: true });
/** POST /settings/org-deletion/:id/cancel (settings.ts:372): stopping is always allowed. */
export const settingsOrgDeletionCancel = route<{ real: true; request: OrgDeletionRequest }, void, { reason?: string }>()(
  'POST', `${A}/settings/org-deletion/:id/cancel`, { honest: true });
/** POST /settings/purge-sample (settings.ts:418): Super Admin; refuses anything that is not sample. */
export const settingsPurgeSample = route<PurgeSampleResult, void, { organizationId: string; confirm: true }>()('POST', `${A}/settings/purge-sample`, { honest: true });
/** POST /settings/rotate-all-keys (settings.ts:439): Super Admin; atomic; lists who lost access. */
export const settingsRotateAllKeys = route<RotateAllKeysResult, void, { tenantId: string; confirm: true }>()('POST', `${A}/settings/rotate-all-keys`, { honest: true });

// ── Users & Permissions ─────────────────────────────────────────────────────

/** One on-call role at a building: who is paged first for an alert there. */
export type FacilityOnCallRole = 'CHARGE_NURSE' | 'AIDE' | 'ADMIN_ON_DUTY';
/** A repeating on-call shift (on_call_shifts). days: 0 Sunday to 6 Saturday; start = end is all day. */
export interface FacilityOnCallShift {
  id: string;
  role: FacilityOnCallRole;
  user_id: string;
  days: number[];
  start_local: string;
  end_local: string;
  timezone: string;
  effective_from: string | null;
  effective_to: string | null;
}
/** GET /organizations/:id/escalation (router.ts): the building's paging plan and who is on call now. */
export interface FacilityEscalation {
  organizationId: string;
  settings: Record<string, unknown>;
  shifts: FacilityOnCallShift[];
  overrides: Array<{ id: string; role: FacilityOnCallRole; user_id: string; starts_at: string; ends_at: string; reason: string | null }>;
  onNow: Record<FacilityOnCallRole, Array<{ id: string; name: string | null; email: string | null; phone: string | null }>>;
  uncoveredHoursNext7Days: Partial<Record<FacilityOnCallRole, number>>;
}
export const facilityEscalation = route<FacilityEscalation>()('GET', `${A}/organizations/:id/escalation`);
/** The paging plan in force (paging-plan.ts effectiveSettings): seconds per step, re-page, the 911 policy, and the limits. */
export interface EscalationSettings {
  criticalStepSec: number;
  highStepSec: number;
  mediumStepSec: number;
  repageSec: number;
  criticalCallPolicy: 'flag' | 'prompt';
  highAdminAfterSec: number;
  maxSecondsTo911: number;
  /** "I don't feel safe" (AF-1608): taps this close are one press (5 to 30 s), and a press this soon after a close opens a linked incident one tier higher (5 to 240 min). A repeat press always escalates: there is no setting for that. */
  unsafePressDebounceSec?: number;
  unsafeReopenLookbackMin?: number;
  limits: { steps: Record<'CRITICAL' | 'HIGH' | 'MEDIUM', { min: number; max: number; def: number }>; repage: { min: number; max: number; def: number } };
}
/** PUT /organizations/:id/escalation-settings (router.ts): the building's admin or Aithora; values outside the limits are refused, not clamped. */
export const facilityEscalationSave = route<
  { organizationId: string; settings: EscalationSettings },
  void,
  Partial<Pick<EscalationSettings, 'criticalStepSec' | 'highStepSec' | 'mediumStepSec' | 'repageSec' | 'criticalCallPolicy' | 'unsafePressDebounceSec' | 'unsafeReopenLookbackMin'>>
>()('PUT', `${A}/organizations/:id/escalation-settings`);
/** POST /organizations/:id/on-call/shifts: the building's admin (or Aithora) adds a shift. */
export const facilityOnCallShiftAdd = route<
  { shift: FacilityOnCallShift },
  void,
  { role: FacilityOnCallRole; userId: string; days: number[]; start: string; end: string; timezone: string; effectiveFrom?: string; effectiveTo?: string }
>()('POST', `${A}/organizations/:id/on-call/shifts`);
/** DELETE /organizations/:id/on-call/shifts/:entryId */
export const facilityOnCallShiftRemove = route<{ ok: boolean }>()('DELETE', `${A}/organizations/:id/on-call/shifts/:entryId`);

/** GET /facilities/:id/staff (settings.ts:485): ':id' may be 'me'. Aithora staff are never returned to a Facility Admin. */
export const facilityStaff = route<FacilityStaffResponse>()('GET', `${A}/facilities/:id/staff`, { honest: true });
/** PUT /facilities/:id/staff/:userId/npi {npi | null}: a staff member's own NPI (Luhn-checked; the building's admin or Aithora). */
export const facilityStaffNpi = route<{ real: true; userId: string; npi: string | null; unchanged?: boolean }, void, { npi: string | null }>()(
  'PUT', `${A}/facilities/:id/staff/:userId/npi`, { honest: true });
/** PUT /facilities/:id/staff/:userId/credential (v3/settings/med-rules.ts): a staff member's license or permit (type null retires it). The building's admin or Aithora; audited. */
export const facilityStaffCredential = route<{ real: true; userId: string; credential: StaffCredential | null; unchanged?: boolean }, void, StaffCredentialBody>()(
  'PUT', `${A}/facilities/:id/staff/:userId/credential`, { honest: true });
/** GET /facilities/:id/med-rules (v3/settings/med-rules.ts): who may give which medicine here: the loaded rule set, its status and citations, or the fail-safe. */
export const facilityMedRules = route<MedRulesView>()('GET', `${A}/facilities/:id/med-rules`, { honest: true });
/** PUT /facilities/:id/med-rules {mode?, override?}: enforce or warn, and a stricter override (never looser). Facility Admin or Aithora; where the building is: Aithora only. Audited. */
export const facilityMedRulesSave = route<MedRulesView, void, { mode?: 'enforce' | 'warn'; override?: unknown; careSetting?: string | null; state?: string | null; country?: string | null }>()(
  'PUT', `${A}/facilities/:id/med-rules`, { honest: true });
/** PUT /facilities/:id/staff/:userId/role (settings.ts:526): Facility Admin or Care Staff only. */
export const facilityStaffRole = route<{ real: true; userId?: string; role: string; unchanged?: boolean }, void, { role: string; /** Nurse or CNA, for a care-staff role (users.care_title). */ title?: 'Nurse' | 'CNA' }>()(
  'PUT', `${A}/facilities/:id/staff/:userId/role`, { honest: true });
/** GET /users/:id/phone (settings.ts:598) */
export const userAlertPhone = route<AlertPhone>()('GET', `${A}/users/:id/phone`, { honest: true });
/** POST /users/:id/phone (settings.ts:621): texted a code; takes over once confirmed. */
export const userAlertPhoneSet = route<AlertPhoneSent, void, { phone: string }>()('POST', `${A}/users/:id/phone`, { honest: true });
/** POST /users/:id/phone/resend (settings.ts:639) */
export const userAlertPhoneResend = route<AlertPhoneSent, void, Record<string, never>>()('POST', `${A}/users/:id/phone/resend`, { honest: true });
/** POST /users/:id/phone/verify (settings.ts): consent is the person's own text and call box
 *  (unchecked by default, not needed to verify); it is recorded only for the person themself. */
export const userAlertPhoneVerify = route<{ real: true; phone: string | null; verified: true; consent?: ConsentOnFile }, void, { code: string; consent?: boolean; source?: 'console' | 'app' }>()('POST', `${A}/users/:id/phone/verify`, { honest: true });
/** POST /users/:id/phone/test (v3/settings/govern.ts): a test text to the caller's own verified alert number. */
export const userAlertPhoneTest = route<{ real: true; live: boolean; reason?: string }, void, Record<string, never>>()('POST', `${A}/users/:id/phone/test`, { honest: true });
/** POST /users/:id/phone/change-request (govern.ts): "Ask my admin to change"; a bell alert to each Facility Admin. */
export const userAlertPhoneChangeRequest = route<{ real: true; sent: boolean }, void, Record<string, never>>()('POST', `${A}/users/:id/phone/change-request`, { honest: true });
/** GET /facilities/:id/sms-roles (govern.ts): which staff get which alerts by text. */
export const facilitySmsRoles = route<SmsRolesResponse>()('GET', `${A}/facilities/:id/sms-roles`, { honest: true });
/** PUT /facilities/:id/sms-roles (govern.ts): Critical is always on and cannot be sent. */
export const facilitySmsRolesSave = route<SmsRolesResponse, void, { role: string; handoffs?: boolean; signatures?: boolean }>()(
  'PUT', `${A}/facilities/:id/sms-roles`, { honest: true });

/** GET /admin/users (router.ts:8362): ?scope=platform for Aithora's team; a Facility Admin gets their own building. */
export const adminUsers = route<AdminUsersResponse, { scope?: 'platform'; organizationId?: string }>()('GET', `${A}/admin/users`);
/** POST /admin/users (router.ts:8406): an Operator's new account waits for the owner. */
export const adminUserCreate = route<AdminUserCreated, void, AdminUserCreateBody>()('POST', `${A}/admin/users`);
/**
 * POST /admin/invites {email, name?, role?, careTitle?, organizationId?}: a one-time invite
 * link the person opens to set their own password (and two-factor, where their role needs
 * it). Nothing is emailed: the link comes back here to send. An Operator's invite waits
 * for the owner's approval (pendingApproval).
 */
export const adminInviteCreate = route<
  { ok: boolean; id: string; inviteToken: string; inviteUrl: string; pendingApproval?: boolean; approvalId?: string },
  void,
  { email: string; name?: string; role?: string; careTitle?: 'Nurse' | 'CNA'; organizationId?: string; /** RESIDENT: the roster resident this sign-in is for. */ residentId?: string }
>()('POST', `${A}/admin/invites`);
/** One row of GET /admin/invites: an invite and where it stands. */
export interface AdminInvite {
  id: string;
  email: string;
  name: string | null;
  role: string;
  organization_id: string | null;
  /** pending (link works), awaiting_approval (the owner has not approved it yet), accepted, revoked. */
  status: string;
  expires_at: string;
  created_at: string;
}
/**
 * GET /admin/invites?organizationId= (router.ts): the invites made, newest first. A
 * Facility Admin gets their own building's; Aithora (Super Admin, Operator) every
 * building's, or one with organizationId. No token or link comes back.
 */
export const adminInvites = route<{ invites: AdminInvite[] }, { organizationId?: string }>()('GET', `${A}/admin/invites`);
/** POST /admin/users/bulk (router.ts:8479) */
export const adminUsersBulk = route<AdminUsersBulkResult, void, { users: AdminUserCreateBody[] }>()('POST', `${A}/admin/users/bulk`);
/** PUT /admin/users/:id (router.ts:8553): role, name, status, multiSig. Team accounts: the owner only. */
export const adminUserUpdate = route<{ success: boolean; user: AdminUser }, void, AdminUserUpdateBody>()('PUT', `${A}/admin/users/:id`);
/**
 * DELETE /admin/users/:id: the full account deletion (account-deletion.ts). Refuses the
 * owner and a facility's last active Admin; a resident only by their facility's Admin.
 */
export const adminUserDelete = route<{ success: boolean; kind?: string; subscription?: string | null }>()('DELETE', `${A}/admin/users/:id`);
/** GET /admin/users/:id/sessions (router.ts:8731) */
export const adminUserSessions = route<AdminUserSessions>()('GET', `${A}/admin/users/:id/sessions`);
/** POST /admin/users/:id/reset-link: email a one-time link to choose a new password (24 hours). `reason` says why it was not sent. */
export const adminUserResetLink = route<{ real: true; sent: boolean; reason: string | null; expiresAt: string }, void, Record<string, never>>()('POST', `${A}/admin/users/:id/reset-link`, { honest: true });
/** GET /admin/access-requests?status= (router.ts:8840): Aithora only. */
export const adminAccessRequests = route<AccessRequestsResponse, { status?: 'pending' | 'approved' | 'denied' }>()('GET', `${A}/admin/access-requests`);
/** POST /admin/facilities/:orgId/roster (router.ts:9896): residents and staff from a spreadsheet. */
export const facilityRosterImport = route<RosterImportResult, void, { rows: RosterRow[] }>()('POST', `${A}/admin/facilities/:orgId/roster`);

// ── Organization ────────────────────────────────────────────────────────────

/** GET /facilities/:id/organization (settings.ts:719): ':id' may be 'me'. */
export const facilityOrganization = route<FacilityOrganizationResponse>()('GET', `${A}/facilities/:id/organization`, { honest: true });
/** PUT /facilities/:id/organization (settings.ts:732): an undo is the same call with reason 'undo'. */
export const facilityOrganizationSave = route<FacilityOrganizationResponse, void, FacilityOrganizationBody>()('PUT', `${A}/facilities/:id/organization`, { honest: true });
/** GET /facilities/:id/contact (settings.ts): the building's phone. ':id' may be 'me'. Its own staff, Aithora and Partner. */
export const facilityContact = route<FacilityContact>()('GET', `${A}/facilities/:id/contact`, { honest: true });
/** GET /api/settings/org-profile (server.ts:3313): the governed organization's profile. */
export const orgProfile = route<OrgProfileResponse>()('GET', '/api/settings/org-profile');
/** PUT /api/settings/org-profile (server.ts:3323): Aithora's operator tier. */
export const orgProfileSave = route<OrgProfileSaved, void, Partial<OrgProfile> & { reason?: string }>()('PUT', '/api/settings/org-profile');
/** PUT /api/settings (server.ts:3197): the tenant's settings; confirm when the default posture changes. */
export const tenantSettingsSave = route<TenantSettingsSaved, void, TenantSettingsBody>()('PUT', '/api/settings');
/**
 * POST /api/settings/reset {confirm}: every tenant setting back to its default (the org
 * profile is left alone). Operator tier; the owner alone when the defaults would lower
 * the default response level.
 */
export const settingsReset = route<TenantSettingsSaved, void, { confirm: true }>()('POST', '/api/settings/reset');
/** GET /usage?organizationId=: one building's plan, usage and per-category chat limits (staff; own building unless Aithora). */
export const orgUsage = route<OrgUsage, { organizationId?: string; period?: string }>()('GET', `${A}/usage`);
/** PATCH /organizations/:id {limits}: per-category chat limits, monthly and per minute (-1 = unlimited). Aithora only. */
export const orgLimitsSave = route<{ organization: unknown; limits: LlmLimit[] }, void, { limits: Partial<Record<'general' | 'financial', { monthly: number; perMin: number }>> }>()(
  'PATCH', `${A}/organizations/:id`);
/** PUT /api/tenants/:id (server.ts:3415): the governed organization's name. */
export const tenantRename = route<{ success: boolean }, void, { name: string }>()('PUT', '/api/tenants/:id');

// ── Security & Keys (server.ts) ─────────────────────────────────────────────

/** GET /api/settings/api-keys (server.ts:3381): active keys only. */
export const apiKeys = route<ApiKeysResponse>()('GET', '/api/settings/api-keys');
/** POST /api/settings/api-keys (server.ts:3348): the secret is shown this once. */
export const apiKeyCreate = route<ApiKeyCreated, void, { name: string; permissions?: ('gate:check' | 'read')[] }>()('POST', '/api/settings/api-keys');
/** DELETE /api/settings/api-keys/:keyId?confirm=true (server.ts:3393): the key fails on its next call. */
export const apiKeyRevoke = route<{ success: boolean }, { confirm: true }>()('DELETE', '/api/settings/api-keys/:keyId');
/** POST /api/tenants/:id/rotate-key (server.ts:3435): the old key fails at once. */
export const tenantKeyRotate = route<TenantKeyRotated, void, { confirm: true }>()('POST', '/api/tenants/:id/rotate-key');

// ── Guard & Response (server.ts) ────────────────────────────────────────────

/** GET /api/guard (server.ts:1921) */
export const guardStatus = route<GuardStatus>()('GET', '/api/guard');
/** POST /api/guard/level (server.ts:1925): raising needs an operator; lowering needs the owner. */
export const guardLevel = route<GuardLevelResult, void, { level: number; reason?: string; confirm: true }>()('POST', '/api/guard/level');
/** POST /api/guard/recover (server.ts:1948): back to SAFE; the owner only. */
export const guardRecover = route<GuardLevelResult, void, { reason?: string }>()('POST', '/api/guard/recover');
/** POST /api/guard/state (server.ts): tightening needs an operator, loosening the owner; confirm-first. */
export const guardStateSet = route<GuardLevelResult, void, { state: 'SAFE' | 'DEGRADED_READ_ONLY' | 'HALTED'; confirm: true }>()('POST', '/api/guard/state');

// ── Two-factor (router.ts, v3/settings.ts) ──────────────────────────────────

/** GET /2fa/status (govern.ts): the signed-in person's enrolment. */
export const twofaStatus = route<TwofaStatus>()('GET', `${A}/2fa/status`, { honest: true });
/** POST /2fa/enroll (router.ts:7250) with a session: a new secret and the backup codes. */
export const twofaSessionEnroll = route<TwofaSessionEnroll, void, Record<string, never>>()('POST', `${A}/2fa/enroll`);
/** POST /2fa/verify-enrollment (router.ts:7311) with a session. */
export const twofaSessionVerify = route<{ ok: true; enabled: true }, void, { code: string }>()('POST', `${A}/2fa/verify-enrollment`);
/** POST /2fa/disable (router.ts:7447): re-verifying a current factor is the confirmation. */
export const twofaDisable = route<{ ok: true; enabled: false }, void, { code?: string; backupCode?: string }>()('POST', `${A}/2fa/disable`);
/** POST /2fa/backup-codes/regenerate (settings.ts:1176): re-auth and confirm; old codes die. */
export const twofaBackupRegenerate = route<{ real: true; backupCodes: string[]; warning: string }, void, { code?: string; password?: string; confirm: true }>()(
  'POST', `${A}/2fa/backup-codes/regenerate`, { honest: true });
/** GET /2fa/passkey/list (router.ts:7820) */
export const passkeyList = route<{ passkeys: Passkey[] }>()('GET', `${A}/2fa/passkey/list`);
/** POST /2fa/passkey/delete (router.ts:7840): re-auth with a password or a code. */
export const passkeyDelete = route<{ ok: true; deleted: number }, void, { credentialId: string; password?: string; code?: string; confirm: true }>()('POST', `${A}/2fa/passkey/delete`);
/** POST /2fa/passkey/register-options (router.ts:7521) with a session: WebAuthn creation options (base64url JSON). */
export const passkeyRegisterOptions = route<Record<string, unknown>, void, Record<string, never>>()('POST', `${A}/2fa/passkey/register-options`);
/** POST /2fa/passkey/register-verify (router.ts:7565): the new credential. */
export const passkeyRegisterVerify = route<{ ok: true; verified: true; credentialId?: string }, void, { response: unknown; deviceName?: string }>()('POST', `${A}/2fa/passkey/register-verify`);
/** GET /2fa/trusted-devices (router.ts:7864): revoked rows included. */
export const trustedDevices = route<{ devices: TrustedDevice[] }>()('GET', `${A}/2fa/trusted-devices`);
/** POST /2fa/trusted-devices/revoke (router.ts:7882) */
export const trustedDeviceRevoke = route<{ ok: true; revoked: number }, void, { id?: string }>()('POST', `${A}/2fa/trusted-devices/revoke`);

// ── Notifications (settings.ts:1031-1170) ───────────────────────────────────

/** GET /settings/notifications/events: every event this role receives, with the person's choice. */
export const notifEvents = route<NotifEventsResponse>()('GET', `${A}/settings/notifications/events`, { honest: true });
/** PUT /settings/notifications/events: one refused choice refuses the whole save. */
export const notifEventsSave = route<NotifEventsSaved, void, { events: { eventKey: string; enabled: boolean; channels: NotifChannel[] }[] }>()(
  'PUT', `${A}/settings/notifications/events`, { honest: true });
/** GET /settings/push-devices: the caller's own devices and what can deliver today. */
export const pushDevices = route<PushDevicesResponse>()('GET', `${A}/settings/push-devices`, { honest: true });
/** POST /devices/:id/test */
export const pushDeviceTest = route<DeviceTestResult, void, Record<string, never>>()('POST', `${A}/devices/:id/test`, { honest: true });
/** DELETE /devices/:id */
export const pushDeviceRemove = route<{ real: true; removed: true }>()('DELETE', `${A}/devices/:id`, { honest: true });
/** GET /settings/notifications/prefs (govern.ts): quiet hours and the SMS fallback. */
export const notifPrefs = route<NotifPrefs>()('GET', `${A}/settings/notifications/prefs`, { honest: true });
/** PUT /settings/notifications/prefs (govern.ts) */
export const notifPrefsSave = route<NotifPrefs, void, Partial<Pick<NotifPrefs, 'quietHours' | 'quietFrom' | 'quietTo' | 'smsFallbackMinutes' | 'pushEnabled' | 'textBackup'>>>()('PUT', `${A}/settings/notifications/prefs`, { honest: true });

// ── Guardian rules and Voice & Scribe (settings.ts:882-1025) ────────────────

/** GET /settings/guardian-rules: Aithora's default for every building. */
export const guardianRulesDefault = route<GovernedDefault<GuardianRules>>()('GET', `${A}/settings/guardian-rules`, { honest: true });
/** PUT /settings/guardian-rules: Aithora only; applies directly. */
export const guardianRulesDefaultSave = route<GovernedSaved<GuardianRules>, void, Partial<GuardianRules>>()('PUT', `${A}/settings/guardian-rules`, { honest: true });
/** GET /facilities/:id/guardian-rules: the building's own and its waiting requests. */
export const facilityGuardianRules = route<GovernedFacility<GuardianRules>>()('GET', `${A}/facilities/:id/guardian-rules`, { honest: true });
/** PUT /facilities/:id/guardian-rules: a Facility Admin's change waits for Aithora (202). */
export const facilityGuardianRulesSave = route<GovernedSaved<GuardianRules>, void, Partial<GuardianRules>>()('PUT', `${A}/facilities/:id/guardian-rules`, { honest: true });
/** GET /settings/voice-scribe */
export const voiceScribeDefault = route<GovernedDefault<VoiceScribeSettings>>()('GET', `${A}/settings/voice-scribe`, { honest: true });
/** PUT /settings/voice-scribe */
export const voiceScribeDefaultSave = route<GovernedSaved<VoiceScribeSettings>, void, Partial<VoiceScribeSettings>>()('PUT', `${A}/settings/voice-scribe`, { honest: true });
/** GET /facilities/:id/voice-scribe */
export const facilityVoiceScribe = route<GovernedFacility<VoiceScribeSettings>>()('GET', `${A}/facilities/:id/voice-scribe`, { honest: true });
/** PUT /facilities/:id/voice-scribe */
export const facilityVoiceScribeSave = route<GovernedSaved<VoiceScribeSettings>, void, Partial<VoiceScribeSettings>>()('PUT', `${A}/facilities/:id/voice-scribe`, { honest: true });

// ── On-call (v3/pulse.ts:451-481; the read is pulseOncallV3) ────────────────

/** PUT /pulse-v3/oncall/steps/:n: who, their phone and the wait before the next step. */
export const oncallStepSave = route<{ real: true; steps: unknown[] }, void, OncallStepBody>()('PUT', `${A}/pulse-v3/oncall/steps/:n`, { honest: true });
/** POST /pulse-v3/oncall/steps/:n/test: a test text to that step. */
export const oncallStepTest = route<{ real: true; live: boolean; reason: string | null; page_id: string }, void, Record<string, never>>()(
  'POST', `${A}/pulse-v3/oncall/steps/:n/test`, { honest: true });
/** PUT /pulse-v3/oncall/rotation: members, daily or weekly, the hand-off time. */
export const oncallRotationSave = route<{ real: true; rotation: unknown; current: unknown }, void, OncallRotationBody>()('PUT', `${A}/pulse-v3/oncall/rotation`, { honest: true });

// ── Daily schedule (router.ts:5975, 6005) ───────────────────────────────────

/** GET /facility-schedule: the building's meal and activity times. */
export const facilitySchedule = route<ScheduleResponse, { organizationId?: string }>()('GET', `${A}/facility-schedule`);
/** PUT /facility-schedule: replaces the whole list. */
export const facilityScheduleSave = route<{ ok: boolean; count: number }, void, ScheduleSaveBody>()('PUT', `${A}/facility-schedule`);

// ── Data & Retention (server.ts) ────────────────────────────────────────────

/** POST /api/settings/export (server.ts:3253): settings.json as a download (read with client.raw). */
export const SETTINGS_EXPORT_PATH = '/api/settings/export';

// ── Compliance (server.ts) ──────────────────────────────────────────────────

/** GET /api/compliance/ai-act/export (server.ts:2874): decisions by risk class and the override rate. */
export const aiActExport = route<AiActExport>()('GET', '/api/compliance/ai-act/export');
/** GET /api/frameworks/enabled (server.ts): which frameworks this tenant has enabled. */
export const frameworksEnabled = route<EnabledFrameworks>()('GET', '/api/frameworks/enabled', { honest: true });
/** POST /api/frameworks/:id/disable (server.ts): the owner only; removes the framework's policies. */
export const frameworkDisable = route<{ success: boolean }, void, { confirm: true }>()('POST', '/api/frameworks/:id/disable');
/** The audit exports (server.ts:2309, 2316), downloads read with client.raw. */
export const AUDIT_EXPORT_JSON_PATH = '/api/audit/export/json';
export const AUDIT_EXPORT_CSV_PATH = '/api/audit/export/csv';
export const AI_ACT_EXPORT_PATH = '/api/compliance/ai-act/export';

// ── Integrations (router.ts:13127-13223, settings.ts:793) ───────────────────

/** GET /integrations/catalog: every system Aithora can connect, by category. */
export const integrationCatalog = route<IntegrationCatalogResponse>()('GET', `${A}/integrations/catalog`);
/** GET /integrations: this building's configured systems (credentials masked). */
export const integrations = route<IntegrationsResponse>()('GET', `${A}/integrations`);
/** POST /integrations: connect a catalog system with its credentials. */
export const integrationCreate = route<{ ok: boolean; id: string }, void, IntegrationCreateBody>()('POST', `${A}/integrations`);
/** DELETE /integrations/:id?confirm=true: data stops arriving; what arrived stays. */
export const integrationRemove = route<{ ok: boolean }, { confirm: true }>()('DELETE', `${A}/integrations/:id`);
/** GET /facilities/:id/devices: each wearable's connector status, set up here, residents linked. */
export const facilityDevices = route<FacilityDevicesResponse>()('GET', `${A}/facilities/:id/devices`, { honest: true });
/** POST /integrations/:id/test: reach the connected system once and report (org-scoped). */
export const integrationTest = route<IntegrationTestResult, void, Record<string, never>>()('POST', `${A}/integrations/:id/test`);
/**
 * GET /api/webhooks/routes (api/webhook.ts): this tenant's webhook routes. Each one is both
 * an inbound address (/api/webhooks + inboundPath) and the outbound URL passing events are
 * forwarded to.
 */
export const webhookRoutes = route<WebhookRoutesResponse>()('GET', '/api/webhooks/routes');
/** POST /api/webhooks/routes {name, forwardUrl}: a new route (operator tier or the system's own key). */
export const webhookRouteCreate = route<{ success: boolean; route: WebhookRouteRow }, void, { name: string; forwardUrl: string }>()('POST', '/api/webhooks/routes');
/** PATCH /api/webhooks/routes/:id {enabled}: switch a route off (the inbound address stops accepting) or back on. */
export const webhookRouteToggle = route<{ success: boolean; route: WebhookRouteRow }, void, { enabled: boolean }>()('PATCH', '/api/webhooks/routes/:id');
/** The public path the inbound address of a route is served at. */
export const WEBHOOK_INBOUND_PREFIX = '/api/webhooks';

/** The outbound alert webhook (server.ts /api/notifications/webhook): never the secret. */
export interface OutboundAlertWebhook {
  configured: boolean;
  url: string | null;
  enabled: boolean;
  hasSecret: boolean;
  /** null = the default (WARN and above). */
  severities: Array<'INFO' | 'WARN' | 'CRITICAL' | 'KILL'> | null;
}
/** GET /api/notifications/webhook: Aithora operators only. */
export const outboundAlertWebhook = route<OutboundAlertWebhook>()('GET', '/api/notifications/webhook');
/** POST /api/notifications/webhook {url (https), secret?, severities}: an omitted secret keeps the stored one. */
export const outboundAlertWebhookSave = route<{ success: boolean }, void, { url: string; secret?: string; severities: string[] }>()(
  'POST', '/api/notifications/webhook');
/** POST /api/notifications/webhook/test: one test delivery, recorded in the alert history. */
export const outboundAlertWebhookTest = route<{ success: boolean; delivered: boolean; error: string | null; sentAt: string }>()(
  'POST', '/api/notifications/webhook/test');
/** GET /integrations/webhooks (govern.ts): the inbound and outbound routes. */
export const governWebhookRoutes = route<GovernWebhookRoutesResponse>()('GET', `${A}/integrations/webhooks`, { honest: true });
/** POST /integrations/webhooks (govern.ts): add an outbound route (https only). */
export const governWebhookRouteCreate = route<{ real: true; id: string }, void, { name: string; url: string; events: string[] }>()('POST', `${A}/integrations/webhooks`, { honest: true });
/** PUT /integrations/catalog/:id/availability (govern.ts): Aithora hides a system from every facility. */
export const integrationAvailability = route<{ real: true; disabled: boolean }, void, { disabled: boolean }>()('PUT', `${A}/integrations/catalog/:id/availability`, { honest: true });

// ── Models & Spend (router.ts:6089, 6108) ───────────────────────────────────

/** GET /admin/controls?organizationId=: enforcement, budgets and this period's usage per provider. */
export const adminControls = route<AdminControls, { organizationId?: string }>()('GET', `${A}/admin/controls`);
/** POST /admin/controls: enforcement modes and per-provider caps. */
export const adminControlsSave = route<{ ok: boolean }, void, AdminControlsBody>()('POST', `${A}/admin/controls`);
/** GET /settings/model-routing (govern.ts): which Claude tier answers which job (llm.ts obeys it). */
export const modelRouting = route<ModelRouting>()('GET', `${A}/settings/model-routing`, { honest: true });
/** PUT /settings/model-routing (govern.ts): the owner only. */
export const modelRoutingSave = route<ModelRouting, void, { job: string; tier: 'Haiku' | 'Sonnet' | 'Opus' }>()('PUT', `${A}/settings/model-routing`, { honest: true });

// ── Test Lab and Tests (server.ts) ──────────────────────────────────────────

/** POST /api/gate/check (core/gate-check-route.ts): one action through the gate. Recorded to the ledger like any
 *  action, unless dryRun is true: then the full decision comes back and nothing is written (the Test Lab). */
export const gateCheck = route<GateCheckResult, void, { type: string; actor: string; source: string; metadata: Record<string, unknown>; dryRun?: boolean }>()('POST', '/api/gate/check');
/** POST /api/test/batch (server.ts:3062) */
export const testBatch = route<TestBatchResult, void, { actions: { type: string; actor?: string; source?: string; metadata?: Record<string, unknown> }[] }>()('POST', '/api/test/batch');
/** POST /api/test/stress (server.ts:3088) */
export const testStress = route<TestStressResult, void, { actionsPerSecond?: number; durationSeconds?: number; actionTypes?: string[] }>()('POST', '/api/test/stress');
/** POST /api/test/simulate (server.ts:3099) */
export const testSimulate = route<TestSimulateResult, void, { policyName?: string; layer?: string; condition: unknown; action?: 'BLOCK' | 'HOLD' | 'ALERT'; testActions: { type: string; actor?: string; source?: string; metadata?: Record<string, unknown> }[] }>()(
  'POST', '/api/test/simulate');
/** POST /api/tests/run (server.ts:3078): the real jest and flutter suites. */
export const testsRun = route<TestRunSummary, void, Record<string, never>>()('POST', '/api/tests/run');

// ── Consumer sections ───────────────────────────────────────────────────────

/** GET /residents/:id/family-links (router.ts): who is linked to this person. */
export const familyLinks = route<FamilyLinksResponse, { organizationId?: string }>()('GET', `${A}/residents/:id/family-links`);
// residentFamilySharing (GET /residents/:id/family-sharing) is defined once, in residents-routes.ts.
/** POST /family/links/:familyUserId/revoke (router.ts): they stop seeing it straight away. */
export const familyLinkRevoke = route<{ ok: boolean }, void, { organizationId: string | null; residentId: string }>()('POST', `${A}/family/links/:familyUserId/revoke`);
/** POST /family/links/:familyUserId/restore (router.ts): the resident themself; a fresh consent record. */
export const familyLinkRestore = route<{ real: true; ok: boolean }, void, { residentId: string }>()('POST', `${A}/family/links/:familyUserId/restore`, { honest: true });
// familyDashboard (GET /family/dashboard) is defined once, in family-routes.ts.
/** PUT /residents/:id/pronouns (govern.ts): the resident, their building's staff or Aithora. */
export const residentPronouns = route<{ real: true; residentId: string; pronouns: 'she' | 'he' | 'they' | null }, void, { pronouns: 'she' | 'he' | 'they' | null }>()(
  'PUT', `${A}/residents/:id/pronouns`, { honest: true });
/** GET /home/me/preferences (v3/home/preferences.ts): the member's own Aithora Home settings. Member only. */
export const homePreferences = route<HomePreferences>()('GET', `${A}/home/me/preferences`, { honest: true });
/** PUT /home/me/preferences (v3/home/preferences.ts): the member only; share and first write where they are enforced. */
export const homePreferencesSave = route<HomePreferences, void, Partial<Pick<HomePreferences, 'text' | 'aloud' | 'night' | 'autoCall' | 'share' | 'first'>>>()(
  'PUT', `${A}/home/me/preferences`, { honest: true });

// ── Settings defaults other pages read (settings.ts:821, 836) ───────────────

/** GET /settings/defaults?facilityId=: policy approver, worklist defaults, family consent, capture, export recipients. */
export const settingsDefaults = route<{ real: true; facilityId: string | null; defaults: Record<string, { value: unknown; source: string | null }> }, { facilityId?: string }>()(
  'GET', `${A}/settings/defaults`, { honest: true });

// ── Extra phones (v3/settings/extra-phones.ts) ──────────────────────────────
// More numbers that reach the assistant as you (text, call, WhatsApp). Your own only.

export interface ExtraPhones { real: true; phones: Array<{ id: string; phone: string | null; last4: string; verifiedAt: string }>; pendingPhone: string | null; max: number }
/** GET /users/:id/extra-phones: your extra phones (masked) and any code waiting. */
export const userExtraPhones = route<ExtraPhones>()('GET', `${A}/users/:id/extra-phones`, { honest: true });
/** POST /users/:id/extra-phones {phone}: text a code to a new number. */
export const userExtraPhoneAdd = route<{ real: true; pendingPhone: string | null; sent: true }, void, { phone: string }>()('POST', `${A}/users/:id/extra-phones`, { honest: true });
/** POST /users/:id/extra-phones/verify {code}: the code from that text adds the number. */
export const userExtraPhoneVerify = route<{ real: true; added: string | null }, void, { code: string }>()('POST', `${A}/users/:id/extra-phones/verify`, { honest: true });
/** DELETE /users/:id/extra-phones/:phoneId: remove one extra phone (id from userExtraPhones). */
export const userExtraPhoneRemove = route<{ real: true; removed: number }>()('DELETE', `${A}/users/:id/extra-phones/:phoneId`, { honest: true });
